import React, {ReactNode} from 'react'
import PPSelect, {OptionProps} from '../shared/forms/PPSelect'
import {Messages} from '../../Messages'
import {
    InnerMiniTable,
    InnerMiniTableBody,
    StyledTableCaption,
    TableRowName,
} from '../../styledComponents/adminPage/Table'
import PPNumericField from '../shared/forms/PPNumericField'
import {HKConstants} from '../../HKConstants'
import PPTextField from '../shared/forms/PPTextField'
import {numberOrNull} from '../../stringHelper/stringHelpers'
import EventEmitterExAdmin from '../shared/pageBaseComponents/EventEmitterExAdmin'
import {Nullable, Optional} from '../../app/TypeHelpers'
import {addEmptyData, valueOrNoSelected} from '../../componentHelper/selectHelper'
import styled from 'styled-components'
import {EquipmentForParent} from './EquipmentForParent'
import {EquipmentWithChildInfo} from './EquipmentWithChildInfo'
import { Box, TableRow, TableCell, InputAdornment, Table, TableBody } from '@mui/material';
import {SelectChangeEvent} from '@mui/material/Select'

export interface EquipmentFormDataSet {
    equipmentName: string
    explainText: string
    parentEquipmentId: Nullable<number>
    parentEquipmentName: Nullable<string>
    reservationCountMonthly: Nullable<number>
    reservationCountDaily: Nullable<number>
    lotteryEntryCountMonthly: Nullable<number>
}


interface Props {
    eventEmitter: EventEmitterExAdmin
    parentEquipmentsMaster: OptionProps[]
    equipmentForParent: Nullable<EquipmentForParent[]>
    equipmentData?: EquipmentWithChildInfo
    onChange?: (value: EquipmentFormDataSet) => void
}

interface State extends EquipmentFormDataSet {
    parentReservationCountMonthly: Nullable<number>
    parentReservationCountDaily: Nullable<number>
    parentLotteryEntryCountMonthly: Nullable<number>
}

export default class AdminEquipmentForm extends React.Component<Props, State> {

    constructor(props: Readonly<Props>) {
        super(props)

        this.handleEquipmentNameChanged = this.handleEquipmentNameChanged.bind(this)
        this.handleExplainTextChanged = this.handleExplainTextChanged.bind(this)
        this.handleParentEquipmentChanged = this.handleParentEquipmentChanged.bind(this)
        this.handleLotteryEntryCountMonthly = this.handleLotteryEntryCountMonthly.bind(this)
        this.handleReservationCountDaily = this.handleReservationCountDaily.bind(this)
        this.handleReservationCountMonthly = this.handleReservationCountMonthly.bind(this)

        this.callOnChange = this.callOnChange.bind(this)

        this.state = {
            equipmentName: '',
            explainText: '',
            parentEquipmentId: null,
            parentEquipmentName: null,
            reservationCountMonthly: null,
            reservationCountDaily: null,
            lotteryEntryCountMonthly: null,
            parentReservationCountMonthly: null,
            parentReservationCountDaily: null,
            parentLotteryEntryCountMonthly: null,
        }
    }


    findParentEquipmentName(parentEquipmentId: number | null): string | null {
        const parentEuipmentNameProps: Optional<OptionProps> = this.props.parentEquipmentsMaster
            .find((item) => Number(item.value) === parentEquipmentId)
        return parentEuipmentNameProps ? parentEuipmentNameProps.label : null
    }

    findParentEquipment(parentEquipmentId: number | null): Nullable<EquipmentForParent> {
        if (this.props.equipmentForParent === null) {
            return null
        }
        if (parentEquipmentId === null) {
            return null
        }
        const parentEquipmet: EquipmentForParent | undefined =
            this.props.equipmentForParent.find(equipment => equipment.id === parentEquipmentId)
        if (parentEquipmet) {
            return parentEquipmet
        } else {
            return null
        }
    }

    componentDidMount(): void {
        const {equipmentData} = this.props

        if (!equipmentData) {
            return
        }
        const parentEquipmentName: string | null = this.findParentEquipmentName(
            equipmentData.parentEquipmentId,
        )
        const updateParentEquipmentId = parentEquipmentName ? equipmentData.parentEquipmentId : null

        const findParentEquipment = this.findParentEquipment(updateParentEquipmentId)
        let parentReservationCountMonthly: Nullable<number> = null
        let parentReservationCountDaily: Nullable<number> = null
        let parentLotteryEntryCountMonthly: Nullable<number> = null
        if (findParentEquipment !== null) {
            parentReservationCountMonthly = findParentEquipment.reservationCountMonthly
            parentReservationCountDaily = findParentEquipment.reservationCountDaily
            parentLotteryEntryCountMonthly = findParentEquipment.lotteryEntryCountMonthly
        }

        this.setState({
            equipmentName: equipmentData.name,
            explainText: equipmentData.explainText,
            parentEquipmentId: updateParentEquipmentId,
            reservationCountMonthly: equipmentData.reservationCountMonthly,
            reservationCountDaily: equipmentData.reservationCountDaily,
            lotteryEntryCountMonthly: equipmentData.lotteryEntryCountMonthly,
            parentReservationCountMonthly: parentReservationCountMonthly,
            parentReservationCountDaily: parentReservationCountDaily,
            parentLotteryEntryCountMonthly: parentLotteryEntryCountMonthly,
        })
    }

    isSelectedParentEquipment(): boolean {
        return this.state.parentEquipmentId !== null
    }

    callOnChange(): void {
        if (this.props.onChange) {
            this.props.onChange({
                ...this.state,
            })
        }
    }

    handleEquipmentNameChanged(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            equipmentName: event.target.value,
        }, this.callOnChange)
    }

    handleExplainTextChanged(event: React.ChangeEvent<HTMLTextAreaElement>): void {
        this.setState({
            explainText: event.target.value,
        }, this.callOnChange)
    }

    handleParentEquipmentChanged(event: SelectChangeEvent<unknown>): void {
        const parentEquipmentId: number | null = numberOrNull(`${event.target.value}`)
        const findParentEquipment = this.findParentEquipment(parentEquipmentId)
        let parentReservationCountMonthly: Nullable<number> = null
        let parentReservationCountDaily: Nullable<number> = null
        let parentLotteryEntryCountMonthly: Nullable<number> = null
        if (findParentEquipment !== null) {
            parentReservationCountMonthly = findParentEquipment.reservationCountMonthly
            parentReservationCountDaily = findParentEquipment.reservationCountDaily
            parentLotteryEntryCountMonthly = findParentEquipment.lotteryEntryCountMonthly
        }

        this.setState({
            parentEquipmentId: parentEquipmentId,
            parentEquipmentName: this.findParentEquipmentName(parentEquipmentId),
            parentReservationCountMonthly: parentReservationCountMonthly,
            parentReservationCountDaily: parentReservationCountDaily,
            parentLotteryEntryCountMonthly: parentLotteryEntryCountMonthly,
        }, this.callOnChange)
    }

    handleReservationCountMonthly(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            reservationCountMonthly: numberOrNull(event.target.value),
        }, this.callOnChange)
    }

    handleReservationCountDaily(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            reservationCountDaily: numberOrNull(event.target.value),
        }, this.callOnChange)
    }

    handleLotteryEntryCountMonthly(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            lotteryEntryCountMonthly: numberOrNull(event.target.value),
        }, this.callOnChange)
    }

    renderParentEquipmentsSelect(): ReactNode {
        const hasChild = this.props.equipmentData !== undefined &&
            this.props.equipmentData.hasChild
        return (<>
            <PPSelect
                eventEmitter={this.props.eventEmitter}
                data={addEmptyData(this.props.parentEquipmentsMaster, '親設備を選択してください')}
                hint={Messages.HCONA0006.toString()}
                onChange={this.handleParentEquipmentChanged}
                value={valueOrNoSelected(this.state.parentEquipmentId)}
                eventKeys={['parentEquipmentIdIsInvalid', 'parentEquipmentId']}
                disabled={hasChild}
            />
            {
                hasChild && <>
                    <Box mt={'4px'}/>
                    <label>他の設備から既に親設備に指定されているため、親設備を指定できません。</label>
                </>
            }
        </>)
    }

    renderParentEquipmentReserveMonthlyCount(): ReactNode {
        let reservationCountMonthly: string
        if (this.state.parentReservationCountMonthly !== null) {
            reservationCountMonthly = `${this.state.parentReservationCountMonthly}回/月`
        } else {
            reservationCountMonthly = '制限無し'
        }
        return (<>
            {reservationCountMonthly}
        </>)
    }

    renderParentEquipmentReserveDailyCount(): ReactNode {
        let reservationCountDaily: string
        if (this.state.parentReservationCountDaily !== null) {
            reservationCountDaily = `${this.state.parentReservationCountDaily}回/日`
        } else {
            reservationCountDaily = '制限無し'
        }
        return (<>
            {reservationCountDaily}
        </>)
    }

    renderParentEquipmentReserveLotteryCount(): ReactNode {
        let lotteryEntryCount: string
        if (this.state.parentLotteryEntryCountMonthly !== null) {
            lotteryEntryCount = `${this.state.parentLotteryEntryCountMonthly}件/月`
        } else {
            lotteryEntryCount = '制限無し'
        }
        return (<>
            {lotteryEntryCount}
        </>)
    }

    renderParentEquipmentInputForm(): ReactNode {
        return (<>
            <TableRow>
                <TableRowName>予約可能回数</TableRowName>
                <TableCell>
                    <InnerMiniTable>
                        <InnerMiniTableBody>
                            <TableRow>
                                <InnerNoBorderTableCell>
                                    <label>
                                        <CountInput
                                            InputProps={{
                                                inputProps: {
                                                    min: 1,
                                                    max: HKConstants.VMN.FAC.MAX_NUMBER_OF_RESERVATIONS,
                                                },
                                                endAdornment: <InputAdornment position="end">回/月</InputAdornment>,
                                            }}
                                            value={this.state.reservationCountMonthly || ''}
                                            onChange={this.handleReservationCountMonthly}
                                            eventKeys={['reservationCountMonthly']}
                                            eventEmitter={this.props.eventEmitter}
                                        />
                                    </label>
                                </InnerNoBorderTableCell>
                                <InnerNoBorderTableCell>
                                    <label>
                                        {
                                            this.state.parentEquipmentId &&
                                            <div>親設備の設定:</div>
                                        }
                                    </label>
                                </InnerNoBorderTableCell>
                                <InnerNoBorderTableCell>
                                    <label>
                                        {
                                            this.state.parentEquipmentId &&
                                            this.renderParentEquipmentReserveMonthlyCount()
                                        }
                                    </label>
                                </InnerNoBorderTableCell>
                            </TableRow>
                            <TableRow>
                                <InnerNoBorderTableCell>
                                    <label>
                                        <CountInput
                                            InputProps={{
                                                inputProps: {
                                                    min: 1,
                                                    max: HKConstants.VMN.FAC.MAX_NUMBER_OF_RESERVATIONS,
                                                },
                                                endAdornment: <InputAdornment position="end">回/日</InputAdornment>,
                                            }}
                                            value={this.state.reservationCountDaily || ''}
                                            onChange={this.handleReservationCountDaily}
                                            eventKeys={['reservationCountDaily', 'dailyIsGreaterThanMonth']}
                                            eventEmitter={this.props.eventEmitter}
                                        />
                                    </label>
                                </InnerNoBorderTableCell>
                                <InnerNoBorderTableCell>
                                </InnerNoBorderTableCell>
                                <InnerNoBorderTableCell>
                                    <label>
                                        {
                                            this.state.parentEquipmentId &&
                                            this.renderParentEquipmentReserveDailyCount()
                                        }
                                    </label>
                                </InnerNoBorderTableCell>
                            </TableRow>
                        </InnerMiniTableBody>
                    </InnerMiniTable>
                </TableCell>
            </TableRow>

            <TableRow>
                <TableRowName>抽選申込数</TableRowName>
                <TableCell>
                    <InnerMiniTable>
                        <InnerMiniTableBody>
                            <TableRow>
                                <InnerNoBorderTableCell>
                                    <label>
                                        <CountInput
                                            InputProps={{
                                                inputProps: {
                                                    min: 1,
                                                    max: HKConstants.VMN.FAC.MAX_NUMBER_OF_LOTTERY_APPLICATION,
                                                },
                                                endAdornment: <InputAdornment position="end">件/月</InputAdornment>,
                                            }}
                                            value={this.state.lotteryEntryCountMonthly || ''}
                                            onChange={this.handleLotteryEntryCountMonthly}
                                            eventKeys={['lotteryEntryCountMonthly']}
                                            eventEmitter={this.props.eventEmitter}
                                        />
                                    </label>
                                </InnerNoBorderTableCell>
                                <InnerNoBorderTableCell>
                                    <label>
                                        {
                                            this.state.parentEquipmentId &&
                                            <div>親設備の設定:</div>
                                        }
                                    </label>
                                </InnerNoBorderTableCell>
                                <InnerNoBorderTableCell>
                                    <label>
                                        {
                                            this.state.parentEquipmentId &&
                                            this.renderParentEquipmentReserveLotteryCount()
                                        }
                                    </label>
                                </InnerNoBorderTableCell>
                            </TableRow>
                        </InnerMiniTableBody>
                    </InnerMiniTable>
                </TableCell>
            </TableRow>
        </>)
    }

    render(): ReactNode {
        const {equipmentData} = this.props
        return (
            <form>
                <Table>
                    <StyledTableCaption>設備情報</StyledTableCaption>
                    <TableBody>
                        <TableRow>
                            <TableRowName required>設備名</TableRowName>
                            <TableCell>
                                <PPTextField
                                    InputProps={{
                                        inputProps: {
                                            maxLength: HKConstants.VMN.FAC.MAX_LENGTH_OF_EQUIPMENT_NAME,
                                        },
                                    }}
                                    autoFocus
                                    defaultValue={equipmentData ? equipmentData.name : ''}
                                    fullWidth
                                    onChange={this.handleEquipmentNameChanged}
                                    eventKeys={['name']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </TableCell>
                        </TableRow>

                        <TableRow>
                            <TableRowName>説明</TableRowName>
                            <TableCell>
                                <PPTextField
                                    InputProps={{
                                        inputProps: {
                                            maxLength: HKConstants.VMN.FAC.MAX_LENGTH_OF_EQUIPMENT_DESCRIPTION,
                                            minRows: 5,
                                        },
                                    }}
                                    fullWidth
                                    multiline
                                    defaultValue={equipmentData ? equipmentData.explainText : ''}
                                    onChange={this.handleExplainTextChanged}
                                    eventKeys={['explainText']}
                                    eventEmitter={this.props.eventEmitter}
                                ></PPTextField>
                            </TableCell>
                        </TableRow>

                        <TableRow>
                            <TableRowName>親設備</TableRowName>
                            <TableCell>{this.renderParentEquipmentsSelect()}</TableCell>
                        </TableRow>
                        {
                            this.renderParentEquipmentInputForm()
                        }
                    </TableBody>
                </Table>
            </form>
        )
    }
}

const CountInput = styled(PPNumericField)`
  width: 100px;
`
export const InnerNoBorderTableCell = styled(TableCell)`
  padding: 8px!important;
  border-bottom: 0 solid!important;
`
